// SPDX-License-Identifier: Apache-2.0

// FILTER OPERATORS

export const filterOperatorValues = [
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'like',
  'nlike',
  'in',
  'nin',
  'before',
  'after',
  'between',
  'nbetween',
  'nul',
  'nnul',
  'intersects',
  'within',
  'contains'
] as const;

export type FilterOperator = (typeof filterOperatorValues)[number];

export const filterOperators: ReadonlyArray<{ operator: FilterOperator; displayName: string }> = [
  { operator: 'eq', displayName: '=' },
  { operator: 'neq', displayName: '!=' },
  { operator: 'like', displayName: 'filteroperator-LIKE' },
  { operator: 'nlike', displayName: 'filteroperator-NOT LIKE' },
  { operator: 'gt', displayName: '>' },
  { operator: 'gte', displayName: '>=' },
  { operator: 'lt', displayName: '<' },
  { operator: 'lte', displayName: '<=' },
  { operator: 'in', displayName: 'filteroperator-IN' },
  { operator: 'nin', displayName: 'filteroperator-NOT IN' },
  { operator: 'before', displayName: 'filteroperator-BEFORE' },
  { operator: 'after', displayName: 'filteroperator-AFTER' },
  { operator: 'between', displayName: 'filteroperator-BETWEEN' },
  { operator: 'nbetween', displayName: 'filteroperator-NOT BETWEEN' },
  { operator: 'nul', displayName: 'filteroperator-IS NULL' },
  { operator: 'nnul', displayName: 'filteroperator-IS NOT NULL' },
  { operator: 'intersects', displayName: 'filteroperator-INTERSECTS' },
  { operator: 'within', displayName: 'filteroperator-WITHIN' },
  { operator: 'contains', displayName: 'filteroperator-CONTAINS' }
];

export function isFilterOperator(value: string): value is FilterOperator {
  return filterOperatorValues.includes(value as FilterOperator);
}

export function isValuelessFilterOperator(operator: FilterOperator): boolean {
  return ['nul', 'nnul'].includes(operator);
}

export function isTwoSidedFilterOperator(operator: FilterOperator): boolean {
  return ['between', 'nbetween'].includes(operator);
}

export function isListFilterOperator(operator: FilterOperator): boolean {
  return ['in', 'nin'].includes(operator);
}

// ATTRIBUTE TYPE GROUPS
// Organizing attribute types into groups to allow the usage of correct UI widgets

export type AttributeTypeGroup = 'string' | 'number' | 'date' | 'geometry';

export const operatorsByAttributeTypeGroup: Record<AttributeTypeGroup, FilterOperator[]> = {
  string: ['eq', 'neq', 'like', 'nlike', 'nul', 'nnul'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'nbetween', 'nul', 'nnul'],
  date: ['eq', 'neq', 'before', 'after', 'between', 'nbetween', 'nnul'],
  geometry: ['intersects', 'within', 'contains']
};

// LOGICAL FILTER OPERATORS

const logicalFilterOperatorValues = ['and', 'or', 'not'] as const;

export type LogicalFilterOperator = (typeof logicalFilterOperatorValues)[number];

export const logicalFilterOperators: ReadonlyArray<{
  operator: LogicalFilterOperator;
  displayName: string;
}> = [
  { operator: 'and', displayName: 'filteroperator-AND' },
  { operator: 'or', displayName: 'filteroperator-OR' },
  { operator: 'not', displayName: 'filteroperator-NOT' }
];

export function isLogicalFilterOperator(value: string): value is LogicalFilterOperator {
  return logicalFilterOperatorValues.includes(value as LogicalFilterOperator);
}
